// Workspace definitions for the Zen-Browser-style workspace switcher in the
// admin sidebar. Each workspace is its own "world": a brand, a sidebar colour
// scheme, a menu, and a home route. The active workspace is derived from the
// URL (see `workspaceIdForPath`) so deep links and refreshes stay correct —
// the switcher just navigates to a workspace's `homePath`.
//
// Adding a workspace later: add an entry here, give it a `pathPrefix`, wire a
// menu in DashboardContainer, and add its home route in `client/src/index.tsx`.

export type WorkspaceId = "clinic" | "wellness";

export interface WorkspaceTheme {
  /** Sider background (the whole sidebar column). */
  siderBg: string;
  /** Collapse-trigger strip background at the bottom of the Sider. */
  triggerBg: string;
  /** Divider colour for the brand header / footer borders. */
  border: string;
  /** Ant Design dark-Menu token overrides so the menu blends with the Sider. */
  menu: {
    darkItemBg: string;
    darkSubMenuItemBg: string;
    darkItemSelectedBg: string;
    /** Unselected menu-label colour. Optional — omit to keep antd's dark
     *  default (rgba(255,255,255,0.65)); set near-white where the Sider
     *  background needs crisper labels (e.g. the plum wellness column). */
    darkItemColor?: string;
  };
}

export interface WorkspaceDef {
  id: WorkspaceId;
  /** Left-to-right order in the switcher; also drives slide direction. */
  order: number;
  /** Tooltip / aria-label on the switcher button. */
  label: string;
  /** Brand text shown at the top of the sidebar. */
  brand: string;
  /** Emoji shown on the switcher button. */
  emoji: string;
  /** Where the switcher navigates when this workspace is selected. */
  homePath: string;
  /** URL prefix that maps a route back to this workspace. */
  pathPrefix: string;
  theme: WorkspaceTheme;
}

// Ant Design's default dark Sider/Menu navy, kept explicit so the clinic
// workspace renders identically to the pre-workspace sidebar.
const NAVY = "#001529";
const NAVY_TRIGGER = "#002140";
const NAVY_SUBMENU = "#000c17";

/**
 * Darken a `#rrggbb` colour by scaling each channel (factor < 1 = darker).
 * Used to derive the collapse-trigger strip from the sidebar colour so the
 * strip stays a recessed shade of whatever the sidebar is painted — including
 * user repaints via the WellnessCami customization colour picker. Anything
 * non-`#rrggbb` is returned unchanged.
 */
export function darkenHex(hex: string, factor = 0.75): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return hex;
  const rgb = parseInt(match[1], 16);
  const scale = (shift: number) =>
    Math.min(255, Math.max(0, Math.round(((rgb >> shift) & 0xff) * factor)));
  return `#${((scale(16) << 16) | (scale(8) << 8) | scale(0))
    .toString(16)
    .padStart(6, "0")}`;
}

// A deep plum purple for the WellnessCami sidebar. The wellness `menu` token
// block keeps `darkItemColor` at full white rather than antd's dark-Menu
// default (rgba(255,255,255,0.65)) so unselected labels stay crisp against the
// column. The selected item uses the SAME accent blue as the clinic workspace
// (#1677ff) so "selected" reads identically in both sidebars; the collapse
// trigger drops to a darker shade of the sidebar colour (derived, ≈#271433 at
// the default plum) so it recedes into the column.
const PLUM = "#341b44";
const PLUM_TRIGGER = darkenHex(PLUM);
const ACCENT_BLUE = "#1677ff";

export const WORKSPACES: Record<WorkspaceId, WorkspaceDef> = {
  clinic: {
    id: "clinic",
    order: 0,
    label: "DrSuite.ai",
    brand: "DrSuite.ai",
    emoji: "🏥",
    homePath: "/admin/patients",
    pathPrefix: "/admin/wellness", // unused for clinic; clinic is the fallback
    theme: {
      siderBg: NAVY,
      triggerBg: NAVY_TRIGGER,
      border: "#333",
      menu: {
        darkItemBg: NAVY,
        darkSubMenuItemBg: NAVY_SUBMENU,
        darkItemSelectedBg: "#1677ff",
      },
    },
  },
  wellness: {
    id: "wellness",
    order: 1,
    label: "WellnessCami",
    brand: "WellnessCami",
    emoji: "🥗",
    homePath: "/admin/wellness",
    pathPrefix: "/admin/wellness",
    theme: {
      siderBg: PLUM,
      triggerBg: PLUM_TRIGGER,
      border: "rgba(255, 255, 255, 0.18)",
      menu: {
        // Transparent so the plum Sider shows through the menu surface.
        darkItemBg: "transparent",
        darkSubMenuItemBg: "rgba(0, 0, 0, 0.12)",
        darkItemSelectedBg: ACCENT_BLUE,
        // Full white labels on the plum column (see note above).
        darkItemColor: "#fff",
      },
    },
  },
};

export const WORKSPACE_LIST: WorkspaceDef[] = Object.values(WORKSPACES).sort(
  (a, b) => a.order - b.order,
);

export const DEFAULT_WORKSPACE_ID: WorkspaceId = "clinic";

// Route for the WellnessCami customization tab. Kept here so the menu, the
// router, and any links stay in sync on one string.
export const WELLNESS_CUSTOMIZATION_PATH = "/admin/wellness/customization";

/**
 * Map a pathname to its workspace. The clinic workspace is the fallback for
 * every admin route that isn't an explicit workspace prefix.
 */
export function workspaceIdForPath(pathname: string): WorkspaceId {
  if (pathname.startsWith(WORKSPACES.wellness.pathPrefix)) return "wellness";
  return "clinic";
}
